/**
 * Main App Component
 * Root component for ANOUK - Nomade Aventure Voice Agent
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCallStore } from '@/stores/callStore';
import { useConversationStore } from '@/stores/conversationStore';
import { useUIStore } from '@/stores/uiStore';
import { useAgentWebSocket } from '@/hooks/useAgentWebSocket';
import { BackgroundManager } from '@/components/layout/BackgroundManager';
import { VoiceInterface } from '@/components/voice/VoiceInterface';
import { AgentMessage } from '@/components/conversation/AgentMessage';
import { QuickReplies } from '@/components/conversation/QuickReplies';
import { CallStatus } from '@/components/voice/CallStatus';
import { CircuitCardModal } from '@/components/circuits/CircuitCardModal';
import { FavoritesModal } from '@/components/circuits/FavoritesModal';
import { ContactFormModal } from '@/components/contact/ContactFormModal';
import { fadeInUp } from '@/utils/constants';
import { StackedCarousel } from '@/components/circuits/StackedCarousel';
import { SearchingIndicator } from '@/components/circuits/SearchingIndicator';
import { MOCK_CIRCUITS, MOCK_QUICK_REPLIES } from '@/utils/mock-data';


function App() {
  const { callId, callStatus, endCall } = useCallStore();
  const {
    lastAgentMessage,
    quickReplies,
    selectedCircuit,
    availableCircuits,
    setSelectedCircuit,
    currentDestination,
    circuitsUpdateKey,
    reset: resetConversation,
  } = useConversationStore();
  const {
    backgroundImages,
    setBackgroundImages,
    isCircuitModalOpen,
    setIsCircuitModalOpen,
    isFavoritesModalOpen,
    setIsFavoritesModalOpen,
    isContactModalOpen,
    setIsContactModalOpen,
    isLoading,
    loadingMessage,
  } = useUIStore();

  // Detect mobile for animations
  const [isMobile, setIsMobile] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mark as initialized after first render to avoid white screen
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setIsInitialized(true);
    });
  }, []);

  // Load initial background images and check for URL callId
  useEffect(() => {
    // Check for callId in URL (for testing/visualizing specific sessions)
    const params = new URLSearchParams(window.location.search);
    const urlCallId = params.get('callId');
    const testMode = params.get('test') === 'true' || params.get('test') === '1';
    
    if (urlCallId) {
      console.log('[App] Found callId in URL:', urlCallId);
      useCallStore.getState().startCall(urlCallId);
      useCallStore.getState().setCallStatus('active'); // Assume active for visual testing
    }

    // Mode test : charger les circuits mock sans conversation
    if (testMode) {
      console.log('[App] 🧪 Mode test activé - Chargement des circuits mock');
      setIsTestMode(true);
      const { setAvailableCircuits, setQuickReplies, setCurrentDestination } = useConversationStore.getState();
      
      // Charger les circuits mock
      setAvailableCircuits(MOCK_CIRCUITS);
      
      // Ne pas afficher le message d'agent en mode test (pas de transcript blanc)
      // setLastAgentMessage(MOCK_AGENT_MESSAGES[2] || 'Voici quelques circuits qui pourraient vous intéresser !');
      
      // Ajouter des quick replies
      setQuickReplies(MOCK_QUICK_REPLIES.circuits || []);
      
      // Définir une destination
      setCurrentDestination('Islande');
      
      // Simuler un appel actif pour l'affichage
      if (!urlCallId) {
        useCallStore.getState().startCall(`test-${Date.now()}`);
        useCallStore.getState().setCallStatus('active');
      }
    }

    // Set some default nice images if none exist
    const defaultImages = [
      'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800', // Travel
      'https://images.unsplash.com/photo-1551632811-561732d1e306',
      'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1', // Lake
      'https://images.unsplash.com/photo-1494783367193-149034c05e8f', // Road
      'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5',
      
    
    ];

    if (useUIStore.getState().backgroundImages.length === 0) {
      setBackgroundImages(defaultImages);
    }
  }, [setBackgroundImages]);

  // Connect to WebSocket when call is active
  const { sendQuickReply } = useAgentWebSocket(callId);

  /**
   * Handle quick reply selection
   */
  const handleQuickReplySelect = (reply: { id: string; text: string }) => {
    if (import.meta.env.DEV) console.log('[App] Quick reply selected:', reply.id, reply.text);
    const isFavoris = reply.id === 'qr-1' || reply.text?.toLowerCase().includes('favoris');
    const isContact = reply.id === 'qr-2' || reply.text?.toLowerCase().includes('RDV');
    const isNouvelleDiscussion = reply.id === 'qr-3' || reply.text?.toLowerCase().includes('nouvelle discussion');
    if (isFavoris) {
      setIsFavoritesModalOpen(true);
      return;
    }
    if (isContact) {
      setIsContactModalOpen(true);
      return;
    }
    if (isNouvelleDiscussion) {
      resetConversation();
      setIsCircuitModalOpen(false);
      setIsFavoritesModalOpen(false);
      setIsContactModalOpen(false);
      endCall();
      return;
    }
    sendQuickReply(reply.text);
  };

  /**
   * Handle circuit modal close
   */
  const handleCircuitModalClose = () => {
    setIsCircuitModalOpen(false);
  };

  // Show loading state only on very first render
  if (!isInitialized) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white/70 text-sm">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[100dvh] overflow-hidden">
      {/* Layer 1: Background Manager (Map + Carousel) */}
      <BackgroundManager
        destination={currentDestination}
        images={backgroundImages}
      />

      {/* Header fixe (logo + statut) pour éviter la disparition */}
      <motion.div
        variants={fadeInUp}
        initial="initial"
        animate="animate"
        className="fixed top-0 left-0 right-0 z-30 flex items-start justify-between p-4 pt-safe md:p-8 pointer-events-none"
      >
        <div className="pointer-events-auto">
          <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-white drop-shadow-lg leading-tight">
            Voyages d'Aventure
          </h1>
          <div className="flex flex-col gap-0 md:gap-1 mt-0 md:mt-1">
            <p className="text-xs md:text-base text-white/90 font-medium leading-tight">Agent vocal ANOUK</p>
            <span className="text-[10px] md:text-xs text-white/70 italic leading-tight">powered by VOXPERIENCE</span>
          </div>
        </div>

        <div className="pointer-events-auto">
          <CallStatus status={callStatus} />
        </div>
      </motion.div>

      {/* Layer 2: Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-[100dvh] p-4 md:p-8">

        {/* Bouton de test masqué */}

        {/* Voice Interface - Centré et remonté sur mobile avec carousel */}
        <div className={`flex-1 flex ${availableCircuits.length > 0 && isMobile ? 'items-start pt-20' : 'items-center'} justify-center relative z-35`}>
          <VoiceInterface hasCarousel={availableCircuits.length > 0} />
        </div>

        {/* Agent Message - Masqué en mode test */}
        {lastAgentMessage && !isTestMode && (
          <div className="w-full max-w-4xl px-4">
            <AgentMessage message={lastAgentMessage} />
          </div>
        )}

        {/* Quick Replies - FIXED tout en bas sur mobile, z-50 pour être au-dessus du carousel (z-45) et recevoir les touches */}
        {quickReplies.length > 0 && (
          <motion.div 
            className={`w-full ${isMobile ? 'fixed inset-x-0 bottom-1 px-3 pb-safe z-50' : 'max-w-4xl'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{ opacity: 0, y: 20 }}
            transition={{
              type: "spring",
              stiffness: 50,
              damping: 25,
              mass: 1,
            }}
          >
            <QuickReplies
              replies={quickReplies}
              onSelect={handleQuickReplySelect}
            />
          </motion.div>
        )}

        {/* Searching skeleton — shown while ANOUK looks up circuits, before results land.
            Same footprint/position as the carousel so the swap is seamless. */}
        <AnimatePresence>
          {isLoading && availableCircuits.length === 0 && (
            <motion.div
              className="fixed left-0 right-0 md:bottom-auto md:left-auto md:right-12 md:top-1/2 w-full px-4 pb-safe md:pb-4 md:px-0 md:w-auto z-[45] flex flex-col items-center md:items-end gap-4 pointer-events-none"
              style={isMobile ? { bottom: quickReplies.length > 0 ? '40px' : '0px' } : {}}
              initial={{ y: isMobile ? 100 : '-50%', opacity: 0 }}
              animate={{ y: isMobile ? 0 : '-50%', opacity: 1 }}
              exit={{ y: isMobile ? 100 : '-50%', opacity: 0 }}
              transition={{ type: 'spring', stiffness: 50, damping: 25, mass: 1 }}
            >
              <div className="pointer-events-auto w-full max-w-sm md:max-w-none md:w-auto">
                <SearchingIndicator message={loadingMessage} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Trip Preview Card (Hero / Bottom Sheet) */}
        <AnimatePresence>
          {availableCircuits.length > 0 && (
            <motion.div 
              className="fixed left-0 right-0 md:bottom-auto md:left-auto md:right-12 md:top-1/2 w-full px-4 pb-safe md:pb-4 md:px-0 md:w-auto z-[45] flex flex-col items-center md:items-end gap-4 pointer-events-none"
              style={isMobile ? { bottom: quickReplies.length > 0 ? '40px' : '0px' } : {}}
              initial={{ 
                y: isMobile ? 100 : '-50%',
                opacity: 0 
              }}
              animate={{ 
                y: isMobile ? 0 : '-50%',
                opacity: 1 
              }}
              exit={{ 
                y: isMobile ? 100 : '-50%',
                opacity: 0 
              }}
              transition={{
                type: "spring",
                stiffness: 50,
                damping: 25,
                mass: 1,
                delay: 0.1,
              }}
            >
              <div className="pointer-events-auto w-full max-w-sm md:max-w-none md:w-auto">
                <StackedCarousel
                  key={circuitsUpdateKey}
                  circuits={availableCircuits}
                  onSelect={(circuit) => {
                    setSelectedCircuit(circuit);
                    setIsCircuitModalOpen(true);
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Circuit Modal */}
      {selectedCircuit && (
        <CircuitCardModal
          circuit={selectedCircuit}
          isOpen={isCircuitModalOpen}
          onClose={handleCircuitModalClose}
        />
      )}

      {/* Favorites Modal */}
      <FavoritesModal
        isOpen={isFavoritesModalOpen}
        onClose={() => setIsFavoritesModalOpen(false)}
      />

      {/* Contact Modal */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}

export default App;
